/* eslint-disable max-params-unless-injectable -- grouping parameters introduces complex problems */
import { assert, isDefined } from '@sama/js-core';
import { isEmpty, last } from 'lodash';
import { Box3, BufferAttribute, Matrix4, Ray, Vector3 } from 'three';
import { Axis } from '../../renderers/side-view/side-view.component';
import { evaluateBoundingBox, isPointWithinBox } from './box-search';
import {
  AccumulatedGroundAttributes,
  CloudAttributes,
} from './cloud-attributes.service';
import { groundInstanceId } from './segmentation-mask-utils';

export interface SearchBoxQuery {
  worldToLocalMatrix: Matrix4;
  boundingBox: Box3;
  normalAxes: [Vector3, Vector3, Vector3];
  orientedBoxVertices: Vector3[];
}

export interface SearchConfiguration {
  cloud: CloudAttributes | AccumulatedGroundAttributes;
  ray: Ray;
  nbPoints?: number;
  pointsToUse: 'ground' | 'cloud' | 'all';
  searchRadius?: number;
  searchBox?: SearchBoxQuery;
}

export interface Point {
  distanceSq: number;
  position: Vector3;
  index: number;
}

export function findClosestPoints(
  configuration: SearchConfiguration,
  sortPredicate = (a: Point, b: Point) => a.distanceSq - b.distanceSq,
) {
  const initialBoundingBox = new Box3();
  initialBoundingBox.min.setScalar(Number.MIN_SAFE_INTEGER);
  initialBoundingBox.max.setScalar(Number.MAX_SAFE_INTEGER);

  const results: Point[] = [];
  parseSubtree(results, 0, 0, initialBoundingBox, configuration);
  return results.sort(sortPredicate);
}

export function reportAllSubTreePoints(
  results: Point[],
  index: number,
  config: SearchConfiguration,
) {
  const { cloud } = config;

  if (shouldConsiderPoint(config, index)) {
    const position = getVector3(cloud.position, index);
    results.push({
      distanceSq: config.ray.distanceSqToPoint(position),
      position,
      index,
    });
    results.sort((a, b) => a.distanceSq - b.distanceSq);
  }

  const [leftIndex, rightIndex] = getChildIndexes(index);
  if (leftIndex < cloud.position.count) {
    reportAllSubTreePoints(results, leftIndex, config);
  }
  if (rightIndex < cloud.position.count) {
    reportAllSubTreePoints(results, rightIndex, config);
  }
}

function parseSubtree(
  results: Point[],
  axis: Axis,
  index: number,
  boundingBox: Box3,
  config: SearchConfiguration,
) {
  if (!shouldParseSubtree(results, boundingBox, index, config)) {
    return;
  }

  if (shouldConsiderPoint(config, index)) {
    if (isDefined(config.searchRadius)) {
      addIfWithinRadius(results, index, config);
    } else if (config.searchBox) {
      addIfWithinBox(results, index, config);
    } else {
      addIfCloser(results, index, config);
    }
  }

  parseChildSubtrees(results, axis, index, boundingBox, config);
}

function parseChildSubtrees(
  results: Point[],
  axis: Axis,
  index: number,
  boundingBox: Box3,
  config: SearchConfiguration,
) {
  const { cloud, ray } = config;

  const position = getVector3(cloud.position, index);
  const nextAxis = ((axis + 1) % 3) as Axis;
  const [leftIndex, rightIndex] = getChildIndexes(index);

  const leftDistanceSq = ray.distanceSqToPoint(
    getVector3(cloud.position, leftIndex),
  );
  const rightDistanceSq = ray.distanceSqToPoint(
    getVector3(cloud.position, rightIndex),
  );

  const leftBoundingBox = boundingBox.clone();
  leftBoundingBox.max.setComponent(axis, position.getComponent(axis));

  const rightBoundingBox = boundingBox.clone();
  rightBoundingBox.min.setComponent(axis, position.getComponent(axis));

  if (leftDistanceSq < rightDistanceSq) {
    parseSubtree(results, nextAxis, leftIndex, leftBoundingBox, config);
    parseSubtree(results, nextAxis, rightIndex, rightBoundingBox, config);
  } else {
    parseSubtree(results, nextAxis, rightIndex, rightBoundingBox, config);
    parseSubtree(results, nextAxis, leftIndex, leftBoundingBox, config);
  }
}

function shouldParseSubtree(
  results: Point[],
  boundingBox: Box3,
  index: number,
  config: SearchConfiguration,
) {
  if (index >= config.cloud.position.count) {
    return false;
  }

  if (config.searchBox) {
    return evaluateBoundingBox(results, boundingBox, index, config);
  }

  if (isEmpty(results)) {
    return true;
  }

  const farthestResult = last(results)!;
  const expandedBoundingBox = boundingBox
    .clone()
    .expandByScalar(farthestResult.distanceSq);

  return config.ray.intersectsBox(expandedBoundingBox);
}

function addIfCloser(
  results: Point[],
  index: number,
  config: SearchConfiguration,
) {
  const { cloud, nbPoints, ray } = config;
  assert(nbPoints, 'should be called only when using nbPoints');

  const position = getVector3(cloud.position, index);
  const distanceSq = ray.distanceSqToPoint(position);

  if (results.length < nbPoints) {
    results.push({ distanceSq, position, index });
    results.sort((a, b) => a.distanceSq - b.distanceSq);
  } else if (distanceSq < last(results)!.distanceSq) {
    results[nbPoints - 1] = { distanceSq, position, index };
    results.sort((a, b) => a.distanceSq - b.distanceSq);
  }
}

function addIfWithinRadius(
  results: Point[],
  index: number,
  config: SearchConfiguration,
) {
  const { cloud, ray, searchRadius } = config;
  assert(searchRadius, 'should be called only when using search radius');

  const position = getVector3(cloud.position, index);
  const distanceSq = ray.distanceSqToPoint(position);

  if (Math.sqrt(distanceSq) <= searchRadius) {
    results.push({ distanceSq, position, index });
    results.sort((a, b) => a.distanceSq - b.distanceSq);
  }
}

function addIfWithinBox(
  results: Point[],
  index: number,
  config: SearchConfiguration,
) {
  const { cloud, ray, searchBox } = config;
  assert(searchBox, 'should be called only when using search box');

  const position = getVector3(cloud.position, index);
  if (isPointWithinBox(searchBox.worldToLocalMatrix, position)) {
    const distanceSq = ray.distanceSqToPoint(position);

    results.push({ distanceSq, position, index });
    results.sort((a, b) => a.distanceSq - b.distanceSq);
  }
}

function shouldConsiderPoint(config: SearchConfiguration, index: number) {
  const { cloud, pointsToUse } = config;
  const isGround = isGroundPoint(cloud, index);

  return (
    pointsToUse === 'all' ||
    (pointsToUse === 'cloud' && !isGround) ||
    (pointsToUse === 'ground' && isGround)
  );
}

function isGroundPoint(
  cloud: CloudAttributes | AccumulatedGroundAttributes,
  pointIndex: number,
) {
  return (
    !isCloudAttributes(cloud) ||
    cloud.instanceId.getX(pointIndex) === groundInstanceId
  );
}

function getChildIndexes(parentIndex: number) {
  return [2 * parentIndex + 1, 2 * parentIndex + 2];
}

function getVector3(bufferAttribute: BufferAttribute, index: number) {
  return new Vector3(
    bufferAttribute.getX(index),
    bufferAttribute.getY(index),
    bufferAttribute.getZ(index),
  );
}

function isCloudAttributes(
  cloud: CloudAttributes | AccumulatedGroundAttributes,
): cloud is CloudAttributes {
  return isDefined((cloud as CloudAttributes).instanceId);
}
